import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import {
  CourierReport as CourierReportData,
  CourierReportGroup,
  CourierReportRow,
  courierModuleService,
  formatMoney,
} from './courierModuleService';

interface Props {
  /** Şube yöneticisi: rapor bu şubeyle sınırlı, şube seçilemez. */
  lockedBranchId?: string | null;
  onBack?: () => void;
}

const localToday = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
const monthStart = () => `${localToday().slice(0, 8)}01`;
const pct = (v?: number | null) => (v == null ? '—' : `%${v.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`);

/**
 * Kurye raporu: türe, kuryeye, şubeye ya da sipariş kaynağına göre paket, ücret, paket başı ücret,
 * ciroya oranı ve ortalama teslim süresi. Kaynak teslimat defteridir (ücret anlaşmadaki tutardır).
 */
const CourierReport: React.FC<Props> = ({ lockedBranchId, onBack }) => {
  const { t } = useTranslation('salesSystem');
  const { branches } = useAuth();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(localToday());
  const [branch, setBranch] = useState<string>(lockedBranchId || '');
  const [group, setGroup] = useState<CourierReportGroup>('kind');
  const [data, setData] = useState<CourierReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const branchName = useCallback(
    (id?: string | null) => (branches || []).find((b: any) => b.id === id || b.firebaseId === id)?.name || '',
    [branches],
  );

  const load = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    setError(null);
    try {
      setData(await courierModuleService.report({ from, to, branchId: lockedBranchId || branch || null, groupBy: group }));
    } catch (e: any) {
      setData(null);
      setError(e?.message || t('courierModule.report.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [from, to, branch, group, lockedBranchId, t]);

  useEffect(() => { void load(); }, [load]);

  const labelOf = useCallback((r: CourierReportRow): string => {
    switch (data?.groupBy) {
      case 'kind': return t(`courierModule.report.kinds.${r.key}`, { defaultValue: r.key });
      case 'source': return t(`courierModule.report.sources.${r.key}`, { defaultValue: r.key });
      case 'branch': return r.label || branchName(r.key) || t('courierModule.report.noBranch');
      default: return r.label || r.key;
    }
  }, [data?.groupBy, t, branchName]);

  const rows = useMemo(() => data?.rows ?? [], [data]);

  const exportCsv = () => {
    if (!data) return;
    const head = [
      t(`courierModule.report.groups.${data.groupBy}`), t('courierModule.report.cols.packages'), t('courierModule.report.cols.cancelledPaid'),
      t('courierModule.report.cols.fee'), t('courierModule.report.cols.perPackage'), t('courierModule.report.cols.orderAmount'),
      t('courierModule.report.cols.ratio'), t('courierModule.report.cols.avgMinutes'),
    ];
    const n = (v?: number | null) => (v == null ? '' : v.toFixed(2).replace('.', ','));
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = [...rows, { ...data.total, key: 'total', label: t('courierModule.report.total') } as CourierReportRow].map((r) => [
      r.key === 'total' ? t('courierModule.report.total') : labelOf(r), String(r.packages), String(r.cancelledPaid), n(r.fee), n(r.perPackage),
      n(r.orderAmount), n(r.feeRatio), r.avgDeliveryMinutes == null ? '' : String(r.avgDeliveryMinutes),
    ].map(esc).join(';'));
    const csv = '﻿' + [head.map(esc).join(';'), ...lines].join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `kurye_raporu_${data.groupBy}_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const total = data?.total;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {onBack && <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>{t('courierModule.report.back')}</Button>}
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{t('courierModule.report.title')}</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" disabled={!data || rows.length === 0} onClick={exportCsv}>{t('courierModule.report.excel')}</Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
        <TextField size="small" type="date" label={t('courierModule.report.from')} value={from}
          onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label={t('courierModule.report.to')} value={to}
          onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        {!lockedBranchId && (
          <TextField select size="small" label={t('courierModule.report.branch')} value={branch}
            onChange={(e) => setBranch(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">{t('courierModule.report.allBranches')}</MenuItem>
            {(branches || []).map((b: any) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
          </TextField>
        )}
        {lockedBranchId && <Chip label={branchName(lockedBranchId) || lockedBranchId} />}
      </Stack>

      <ToggleButtonGroup size="small" exclusive value={group} onChange={(_, v) => { if (v) setGroup(v); }} sx={{ mb: 2, flexWrap: 'wrap' }}>
        {(['kind', 'courier', 'branch', 'source'] as CourierReportGroup[])
          .filter((g) => !(lockedBranchId && g === 'branch'))
          .map((g) => <ToggleButton key={g} value={g}>{t(`courierModule.report.groups.${g}`)}</ToggleButton>)}
      </ToggleButtonGroup>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}

      {!loading && data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t(`courierModule.report.groups.${data.groupBy}`)}</TableCell>
                <TableCell align="right">{t('courierModule.report.cols.packages')}</TableCell>
                <TableCell align="right">{t('courierModule.report.cols.fee')}</TableCell>
                <TableCell align="right">{t('courierModule.report.cols.perPackage')}</TableCell>
                <TableCell align="right">{t('courierModule.report.cols.ratio')}</TableCell>
                <TableCell align="right">{t('courierModule.report.cols.avgMinutes')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">{t('courierModule.report.empty')}</Typography></TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.key} hover>
                  <TableCell>
                    {labelOf(r)}
                    {data.groupBy === 'courier' && r.kind && (
                      <Chip size="small" variant="outlined" sx={{ ml: 1 }} label={t(`courierModule.report.kinds.${r.kind}`, { defaultValue: r.kind })} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {r.packages.toLocaleString('tr-TR')}
                    {r.cancelledPaid > 0 && <Typography component="span" variant="caption" color="text.secondary">{` +${r.cancelledPaid} ${t('courierModule.report.cancelledShort')}`}</Typography>}
                  </TableCell>
                  <TableCell align="right">{formatMoney(r.fee)}</TableCell>
                  <TableCell align="right">{r.perPackage == null ? '—' : formatMoney(r.perPackage)}</TableCell>
                  <TableCell align="right">{pct(r.feeRatio)}</TableCell>
                  <TableCell align="right">{r.avgDeliveryMinutes == null ? '—' : t('courierModule.report.minutes', { count: r.avgDeliveryMinutes })}</TableCell>
                </TableRow>
              ))}
              {total && rows.length > 0 && (
                <TableRow sx={{ '& td': { fontWeight: 700 } }}>
                  <TableCell>{t('courierModule.report.total')}</TableCell>
                  <TableCell align="right">{total.packages.toLocaleString('tr-TR')}</TableCell>
                  <TableCell align="right">{formatMoney(total.fee)}</TableCell>
                  <TableCell align="right">{total.perPackage == null ? '—' : formatMoney(total.perPackage)}</TableCell>
                  <TableCell align="right">{pct(total.feeRatio)}</TableCell>
                  <TableCell align="right">{total.avgDeliveryMinutes == null ? '—' : t('courierModule.report.minutes', { count: total.avgDeliveryMinutes })}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>{t('courierModule.report.hint')}</Typography>
    </Box>
  );
};

export default CourierReport;
